import type {curriculaPaths, CurriculaOk} from '@freckle/educator-entities/ts/openapi-clients'
import {
  fancyClient,
  curriculaClient,
  extractData,
} from '@freckle/educator-entities/ts/openapi-clients'

type ExtraLabels = {
  // Plural versions of the above labels, from MathSystemConfiguration where we simply add an 's'
  domainsLabel: string
  standardsLabel: string
  // The backend doesn't provide skills labels at all, but probably should. These are faked for now:
  skillLabel: string
  skillsLabel: string
}

type StandardSet = CurriculaOk<curriculaPaths['/standard-sets']>[0]

export type RlStandardSetT = StandardSet & ExtraLabels

export const addExtraLabels = (attrs: StandardSet): RlStandardSetT => ({
  ...attrs,
  domainsLabel: `${attrs.domainLabel}s`,
  standardsLabel: `${attrs.standardLabel}s`,
  skillLabel: 'skill',
  skillsLabel: 'skills',
})

export const fetchRlStandardSets = (
  query: curriculaPaths['/standard-sets']['get']['parameters']['query'],
): Promise<Array<RlStandardSetT>> =>
  curriculaClient
    .GET('/standard-sets', {params: {query}})
    .then(extractData)
    .then(vs => vs.map(addExtraLabels))

type DehydratedStandardSet = {id: string}

export const fetchRlStandardSetForSchool = (schoolId: number): Promise<DehydratedStandardSet> =>
  fancyClient
    .GET('/3/schools/{V3SchoolP-SchoolId}/math/standard-set', {
      params: {path: {'V3SchoolP-SchoolId': schoolId}},
    })
    .then(extractData)

// NB: StandardSet Dark Test users include all Freckle email addresses, and
// are shown StandardSets that lack defined Administrative Areas
export function filterStandardSetsForDarkTest(
  standardSets: Array<RlStandardSetT>,
  email: string,
  selectedStandardSetId?: string | null,
): Array<RlStandardSetT> {
  const hasSelectedStandardSetId =
    selectedStandardSetId !== undefined && selectedStandardSetId !== null
  return isStaffEmail(email)
    ? appendIdToStandardSetDescription(standardSets)
    : standardSets.filter(
        s => s.isLive || (hasSelectedStandardSetId && selectedStandardSetId === s.id),
      )
}

const STAFF_EMAIL_DOMAINS = ['freckle.com', 'renaissance.com', 'renlearn.co.uk']

function isStaffEmail(email: string): boolean {
  return STAFF_EMAIL_DOMAINS.some(domain => email.endsWith(`@${domain}`))
}

// In the dark test and console, there are standard sets that have the same
// name/description label despite being distinct systems. The function below
// appends the ID to the description label
export function appendIdToStandardSetDescription(
  standardSets: Array<RlStandardSetT>,
): Array<RlStandardSetT> {
  return standardSets.map(standardSet => ({
    ...standardSet,
    description: !standardSet.isLive
      ? `${standardSet.description} - ${standardSet.id} (Staff only)`
      : `${standardSet.description} - ${standardSet.id}`,
  }))
}
